import { Box, Button, FormControl, Stack, Typography } from '@mui/material'
import { useTranslate } from '@tolgee/react'
import { usePlanningStore } from '@planning/AppProvider'
import { GateDirectionIcon } from '@planning/pages/GateControl/Atoms/GateDirectionIcon'
import { IGateClerkViewStore } from '@planning/stores/gateClerk/GateClerkViewStore'
import { ContainerMobile, TomAutoComplete, useMinimalsTheme } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import { observer } from 'mobx-react-lite'
import { useEffect } from 'react'
import { Controller, useForm } from 'react-hook-form'

interface GangOption {
  id: number
  name: string
}

interface Props {
  store: IGateClerkViewStore
  onContinueClick: () => void
  gangs: GangOption[]
  onFetchGangs: () => Promise<void>
}

export const GangSelection = observer(({ store, onContinueClick, gangs, onFetchGangs }: Props) => {
  const { t } = useTranslate()
  const theme = useMinimalsTheme()
  const { tenantStore, gateSessionStore } = usePlanningStore()
  const skipGangSelection = tenantStore.skipGangSelection

  useAsyncFetch(async () => {
    if (!skipGangSelection) {
      await onFetchGangs()
    }
  }, [onFetchGangs, skipGangSelection])

  useEffect(() => {
    gateSessionStore.setStorageScope(tenantStore.tenant?.name ?? 'global')
    gateSessionStore.fetch()
  }, [gateSessionStore, tenantStore.tenant?.name])

  // Read the observable here in the observer's own render body — reading it inside the
  // react-hook-form <Controller render={...}> prop below would NOT be tracked by MobX
  // (Controller invokes that closure outside this component's reactive render), so the
  // gate list would never refresh on SignalR updates.
  const gateOptions = gateSessionStore.availableGates

  const {
    control,
    formState: { errors },
    handleSubmit,
  } = useForm({
    defaultValues: {
      selectedGang: store?.selectedGang || undefined,
      selectedGate: gateSessionStore.boundGate ?? gateSessionStore.lastUsedGate ?? undefined,
    },
  })

  const onSubmit = handleSubmit(data => {
    store.setSelectedGang(skipGangSelection ? undefined : data.selectedGang)
    if (data.selectedGate) gateSessionStore.confirm(data.selectedGate)
    onContinueClick()
  })

  return (
    <ContainerMobile
      sx={{
        backgroundColor: theme.palette.grey[200],
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Stack
        component='form'
        noValidate
        autoComplete='off'
        spacing={theme.customSpacing.l}
        justifyContent='center'
        alignItems='center'
        width={300}
        onSubmit={onSubmit}
      >
        <Typography variant='h1' gutterBottom>
          {t('home', 'Home')}
        </Typography>
        <Controller
          control={control}
          name='selectedGate'
          rules={{ required: t('fieldIsRequired', 'Field is required') }}
          render={({ field: { onChange, value } }) => (
            <FormControl fullWidth required>
              <TomAutoComplete
                sx={{ width: '100%' }}
                options={gateOptions}
                required
                entityKey='id'
                entityDisplayValue='name'
                dataCy='select-gate-autocomplete'
                label={t('selectGate', 'Select gate')}
                multiple={false}
                defaultValue={gateOptions.find(option => option.id === value?.id)}
                onChangeEntity={entity => {
                  onChange(entity)
                }}
                error={!!errors.selectedGate}
                renderOption={(props, option) => (
                  <Box
                    {...props}
                    key={option.id}
                    component='li'
                    data-cy={`gate-option-${option.id}`}
                  >
                    <GateDirectionIcon
                      direction={option.direction}
                      fontSize='small'
                      sx={{ mr: 1 }}
                    />
                    <Typography variant='body1'>
                      {option.code ? `${option.code} — ${option.name}` : option.name}
                    </Typography>
                  </Box>
                )}
              />
            </FormControl>
          )}
        />
        {!skipGangSelection && (
          <Controller
            control={control}
            name='selectedGang'
            rules={{ required: t('fieldIsRequired', 'Field is required') }}
            render={({ field: { onChange, value } }) => (
              <FormControl fullWidth required>
                <TomAutoComplete
                  sx={{ width: '100%' }}
                  options={gangs}
                  required
                  entityKey='id'
                  entityDisplayValue='name'
                  dataCy='select-gang-autocomplete'
                  label={t('selectGang', 'Select gang')}
                  multiple={false}
                  defaultValue={gangs.find(option => option.id === value?.id)}
                  onChangeEntity={entity => {
                    onChange(entity)
                  }}
                  error={!!errors.selectedGang}
                  renderOption={(props, option) => (
                    <Box
                      {...props}
                      key={option.id}
                      component='li'
                      data-cy={`gang-option-${option.id}`}
                    >
                      <Typography variant='body1'>{option.name}</Typography>
                    </Box>
                  )}
                />
              </FormControl>
            )}
          />
        )}
        <Button
          variant='contained'
          color='primary'
          fullWidth
          type='submit'
          data-cy='continue-button'
        >
          {t('continue', 'Continue')}
        </Button>
      </Stack>
    </ContainerMobile>
  )
})
